/**
 * <SeoHead /> — auto-injects title / meta / canonical / og:* / twitter:* tags
 * AND JSON-LD schemas into <head> on every route change.
 *
 * Mount ONCE in App.js inside <Router> but outside <Routes>. It calls
 * /api/seo/page-meta?path=<pathname> on every navigation and updates the
 * document head accordingly. Respects the admin toggle: if
 * settings.auto_inject_enabled === false → only updates document.title
 * (so the UI tab name still works) but skips meta/og/schema injection.
 *
 * Pages that manage their own JSON-LD (e.g. /compare/:slug) can opt out
 * by appending a script with id="seo-page-override" — SeoHead leaves
 * those alone (only it removes its own id="seo-auto-*" tags).
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const AUTO_PREFIX  = "seo-auto-";
const META_KEYS_BY_PROPERTY = ["og:title", "og:description", "og:image", "og:url", "og:type"];
const META_KEYS_BY_NAME     = ["description", "twitter:card", "twitter:title", "twitter:description", "twitter:image"];

// In-memory cache so repeat visits are instant
const cache = new Map();

export default function SeoHead() {
  const { pathname } = useLocation();
  const inflight = useRef(null);

  useEffect(() => {
    // Cancel any in-flight request for the previous path
    const myCall = { cancelled: false };
    inflight.current = myCall;

    (async () => {
      try {
        let data = cache.get(pathname);
        if (!data) {
          const r = await api.get(`/seo/page-meta?path=${encodeURIComponent(pathname)}`);
          data = r.data;
          cache.set(pathname, data);
        }
        if (myCall.cancelled) return;

        // Title always updates (safe, minor)
        if (data.title) document.title = data.title;

        // The rest only when admin has enabled auto-inject
        if (!data.settings?.auto_inject_enabled) {
          purgeAutoTags();
          return;
        }

        applyMeta(data, pathname);
        applySchemas(data.schemas || []);
      } catch {
        // silent — never break the app over SEO injection
      }
    })();

    return () => { myCall.cancelled = true; };
  }, [pathname]);

  return null;
}

// ─────────────────────── helpers ───────────────────────
function purgeAutoTags() {
  document.querySelectorAll(`[id^="${AUTO_PREFIX}"]`).forEach(el => el.remove());
}

function ensureTag(id, tagName, attrs = {}) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement(tagName);
    el.id = id;
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  return el;
}

function applyMeta(data, pathname) {
  const origin = window.location.origin;
  const fullUrl = origin + pathname;

  // <link rel="canonical">
  const canon = ensureTag(`${AUTO_PREFIX}canonical`, "link", { rel: "canonical" });
  canon.setAttribute("href", origin + (data.canonical || pathname));

  // meta name=description
  const desc = ensureTag(`${AUTO_PREFIX}description`, "meta", { name: "description" });
  desc.setAttribute("content", data.description || "");

  // Open Graph
  const og = {
    "og:title":       data.title || "",
    "og:description": data.description || "",
    "og:image":       data.og_image || "",
    "og:url":         fullUrl,
    "og:type":        pathname.startsWith("/blog/") ? "article" : "website",
  };
  Object.entries(og).forEach(([prop, content]) => {
    const t = ensureTag(`${AUTO_PREFIX}${prop.replace(":", "-")}`, "meta", { property: prop });
    t.setAttribute("content", content);
  });

  // Twitter
  const tw = {
    "twitter:card":        "summary_large_image",
    "twitter:title":       data.title || "",
    "twitter:description": data.description || "",
    "twitter:image":       data.og_image || "",
  };
  Object.entries(tw).forEach(([name, content]) => {
    const t = ensureTag(`${AUTO_PREFIX}${name.replace(":", "-")}`, "meta", { name });
    t.setAttribute("content", content);
  });
}

function applySchemas(schemas) {
  // Remove only our own JSON-LD blocks (preserve page-specific overrides
  // like the one CompareDetailPage injects via id="compare-jsonld")
  document.querySelectorAll(`script[id^="${AUTO_PREFIX}jsonld-"]`).forEach(el => el.remove());

  schemas.forEach((schema, i) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id   = `${AUTO_PREFIX}jsonld-${i}`;
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);
  });
}
